import bcrypt from "bcrypt";
import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  FEEDBACK_STATUS,
  HelpFeedback,
  School,
  SCHOOL_STATUS,
  Student,
  STUDENT_STATUS,
  Teacher,
  TEACHER_STATUS,
  User,
} from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class SettingsController {
  /**
   * Get user settings and preferences
   * @route GET /api/v1/settings
   * @access Private (requires authentication)
   */
  async getSettings(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const role = authReq.user?.role;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      let roleSpecificSettings: any = {};

      // Get role-specific settings
      switch (role) {
        case "student": {
          const student = await Student.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (student) {
            roleSpecificSettings = {
              notifications: student.preferences?.notifications || {
                email: user.isNotificationEnabled,
                push: true,
                sms: false,
              },
              privacy: student.preferences?.privacy || {
                shareContactInfo: false,
                shareAcademicInfo: false,
                allowDirectMessages: true,
              },
            };
          }

          break;
        }
        case "teacher": {
          const teacher = await Teacher.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (teacher) {
            roleSpecificSettings = {
              notifications: teacher.preferences?.notifications || {
                email: user.isNotificationEnabled,
                push: true,
                sms: false,
              },
              preferences: {
                maxClassSize: teacher.preferences?.maxClassSize,
                preferredSubjects: teacher.preferences?.preferredSubjects || [],
                availableDays: teacher.preferences?.availableDays || [],
              },
            };
          }

          break;
        }
        case "school": {
          const school = await School.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (school) {
            roleSpecificSettings = {
              notifications: {
                email: user.isNotificationEnabled,
                push: true,
                sms: false,
              },
              schoolSettings: school.settings || {
                allowStudentRegistration: true,
                allowTeacherRegistration: true,
                requireDocumentVerification: true,
                autoApproveStudents: false,
                autoApproveTeachers: false,
              },
            };
          }

          break;
        }
        // No default
      }

      res.status(200).json({
        success: true,
        message: "Settings retrieved successfully",
        data: {
          settings: {
            notifications: roleSpecificSettings.notifications || {
              email: user.isNotificationEnabled,
              push: true,
              sms: false,
            },
            privacy: roleSpecificSettings.privacy || {
              shareProfile: true,
              allowMessages: true,
            },
            preferences: roleSpecificSettings.preferences || {
              language: "en",
              timezone: "UTC",
            },
            ...(roleSpecificSettings.schoolSettings && {
              schoolSettings: roleSpecificSettings.schoolSettings,
            }),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get settings error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update notification settings
   * @route PUT /api/v1/settings/notifications
   * @access Private (requires authentication)
   * @body { email?: boolean, push?: boolean, sms?: boolean }
   */
  async updateNotificationSettings(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const { email, push, sms } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Validate input
      if (email !== undefined && typeof email !== "boolean") {
        res.status(400).json({
          success: false,
          message: "Email notification setting must be a boolean",
        } as APIResponse);
        return;
      }

      if (push !== undefined && typeof push !== "boolean") {
        res.status(400).json({
          success: false,
          message: "Push notification setting must be a boolean",
        } as APIResponse);
        return;
      }

      if (sms !== undefined && typeof sms !== "boolean") {
        res.status(400).json({
          success: false,
          message: "SMS notification setting must be a boolean",
        } as APIResponse);
        return;
      }

      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      const role = authReq.user?.role;

      // Update User model notification setting
      if (email !== undefined) {
        await User.findByIdAndUpdate(userId, { isNotificationEnabled: email });
      }

      // Update role-specific notification settings
      if (role === "student") {
        const student = await Student.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (student) {
          if (!student.preferences) {
            student.preferences = {
              notifications: { email: true, push: true, sms: false },
              privacy: {
                shareContactInfo: false,
                shareAcademicInfo: false,
                allowDirectMessages: true,
              },
            };
          }
          if (email !== undefined)
            student.preferences.notifications.email = email;
          if (push !== undefined) student.preferences.notifications.push = push;
          if (sms !== undefined) student.preferences.notifications.sms = sms;
          await student.save();
        }
      } else if (role === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (teacher) {
          if (!teacher.preferences) {
            teacher.preferences = {
              maxClassSize: undefined,
              preferredSubjects: [],
              availableDays: [],
              notifications: { email: true, push: true, sms: false },
            };
          }
          if (email !== undefined)
            teacher.preferences.notifications.email = email;
          if (push !== undefined) teacher.preferences.notifications.push = push;
          if (sms !== undefined) teacher.preferences.notifications.sms = sms;
          await teacher.save();
        }
      }

      logger.info(`Notification settings updated for user: ${user.email}`, {
        userId,
        email,
        push,
        sms,
      });

      // Get updated settings
      let updatedNotifications = {
        email: email === undefined ? user.isNotificationEnabled : email,
        push: push === undefined ? true : push,
        sms: sms === undefined ? false : sms,
      };

      if (role === "student") {
        const student = await Student.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (student?.preferences?.notifications) {
          updatedNotifications = student.preferences.notifications;
        }
      } else if (role === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (teacher?.preferences?.notifications) {
          updatedNotifications = teacher.preferences.notifications;
        }
      }

      res.status(200).json({
        success: true,
        message: "Notification settings updated successfully",
        data: {
          notifications: updatedNotifications,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update notification settings error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Toggle notification status (simple on/off)
   * @route PUT /api/v1/settings/notifications/toggle
   * @access Private (requires authentication)
   * @body { enabled: boolean }
   */
  async toggleNotifications(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const { enabled } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (typeof enabled !== "boolean") {
        res.status(400).json({
          success: false,
          message: "Enabled must be a boolean value",
        } as APIResponse);
        return;
      }

      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Update notification status
      user.isNotificationEnabled = enabled;
      await user.save();

      logger.info(
        `Notifications ${enabled ? "enabled" : "disabled"} for user: ${user.email}`,
        {
          userId,
          enabled,
        },
      );

      res.status(200).json({
        success: true,
        message: `Notifications ${enabled ? "enabled" : "disabled"} successfully`,
        data: {
          notificationsEnabled: enabled,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Toggle notifications error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update user preferences
   * @route PUT /api/v1/settings/preferences
   * @access Private (requires authentication)
   * @body { language?: string, timezone?: string, theme?: string }
   */
  async updatePreferences(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const { language, timezone, theme } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // For now, we'll just return success since we don't have preference fields in User model
      // In a real implementation, you'd add these fields to the User model or a separate Preferences model

      logger.info(`Preferences updated for user: ${user.email}`, {
        userId,
        language,
        timezone,
        theme,
      });

      const role = authReq.user?.role;

      // Update role-specific preferences
      if (role === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (teacher) {
          if (!teacher.preferences) {
            teacher.preferences = {
              maxClassSize: undefined,
              preferredSubjects: [],
              availableDays: [],
              notifications: { email: true, push: true, sms: false },
            };
          }
          // Note: Teacher preferences don't have language/timezone, but we can add them if needed
          await teacher.save();
        }
      } else if (role === "school") {
        // School preferences can be updated here if needed
      }

      res.status(200).json({
        success: true,
        message: "Preferences updated successfully",
        data: {
          preferences: {
            language: language || "en",
            timezone: timezone || "UTC",
            theme: theme || "light",
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update preferences error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update privacy settings
   * @route PUT /api/v1/settings/privacy
   * @access Private (requires authentication)
   * @body { shareContactInfo?: boolean, shareAcademicInfo?: boolean, allowDirectMessages?: boolean }
   */
  async updatePrivacySettings(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const role = authReq.user?.role;
      const { shareContactInfo, shareAcademicInfo, allowDirectMessages } =
        req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (role !== "student") {
        res.status(403).json({
          success: false,
          message: "Privacy settings are only available for students",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      if (!student.preferences) {
        student.preferences = {
          notifications: { email: true, push: true, sms: false },
          privacy: {
            shareContactInfo: false,
            shareAcademicInfo: false,
            allowDirectMessages: true,
          },
        };
      }

      if (shareContactInfo !== undefined) {
        student.preferences.privacy.shareContactInfo = shareContactInfo;
      }
      if (shareAcademicInfo !== undefined) {
        student.preferences.privacy.shareAcademicInfo = shareAcademicInfo;
      }
      if (allowDirectMessages !== undefined) {
        student.preferences.privacy.allowDirectMessages = allowDirectMessages;
      }

      await student.save();

      logger.info(`Privacy settings updated for student: ${student.fullName}`, {
        userId,
        shareContactInfo,
        shareAcademicInfo,
        allowDirectMessages,
      });

      res.status(200).json({
        success: true,
        message: "Privacy settings updated successfully",
        data: {
          privacy: student.preferences.privacy,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update privacy settings error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update school settings
   * @route PUT /api/v1/settings/school
   * @access Private (School Admin)
   * @body { allowStudentRegistration?: boolean, allowTeacherRegistration?: boolean, requireDocumentVerification?: boolean, autoApproveStudents?: boolean, autoApproveTeachers?: boolean }
   */
  async updateSchoolSettings(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const role = authReq.user?.role;
      const {
        allowStudentRegistration,
        allowTeacherRegistration,
        requireDocumentVerification,
        autoApproveStudents,
        autoApproveTeachers,
      } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (role !== "school") {
        res.status(403).json({
          success: false,
          message: "School settings are only available for school admins",
        } as APIResponse);
        return;
      }

      const school = await School.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!school) {
        res.status(404).json({
          success: false,
          message: "School profile not found",
        } as APIResponse);
        return;
      }

      if (!school.settings) {
        school.settings = {
          allowStudentRegistration: true,
          allowTeacherRegistration: true,
          requireDocumentVerification: true,
          autoApproveStudents: false,
          autoApproveTeachers: false,
        };
      }

      if (allowStudentRegistration !== undefined) {
        school.settings.allowStudentRegistration = allowStudentRegistration;
      }
      if (allowTeacherRegistration !== undefined) {
        school.settings.allowTeacherRegistration = allowTeacherRegistration;
      }
      if (requireDocumentVerification !== undefined) {
        school.settings.requireDocumentVerification =
          requireDocumentVerification;
      }
      if (autoApproveStudents !== undefined) {
        school.settings.autoApproveStudents = autoApproveStudents;
      }
      if (autoApproveTeachers !== undefined) {
        school.settings.autoApproveTeachers = autoApproveTeachers;
      }

      await school.save();

      logger.info(`School settings updated: ${school.schoolName}`, {
        userId,
        schoolId: school.id,
        settings: school.settings,
      });

      res.status(200).json({
        success: true,
        message: "School settings updated successfully",
        data: {
          schoolSettings: school.settings,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update school settings error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Change password (authenticated user)
   * @route PUT /api/v1/settings/change-password
   * @access Private (all roles)
   * @body { currentPassword, newPassword }
   */
  async changePassword(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const userId = authReq.user?.userId;
      const { currentPassword, newPassword } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!currentPassword || !newPassword) {
        res.status(400).json({
          success: false,
          message: "Current password and new password are required",
        } as APIResponse);
        return;
      }

      // Validate new password strength
      if (
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
          newPassword,
        )
      ) {
        res.status(400).json({
          success: false,
          message:
            "Password must contain at least 8 characters with uppercase, lowercase, number and special character",
        } as APIResponse);
        return;
      }

      // Find user (include password for authentication)
      const user = await User.findById(userId).select("+password");
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Verify current password
      if (!user.password) {
        res.status(400).json({
          success: false,
          message: "No password set for user. Please use password reset.",
        } as APIResponse);
        return;
      }

      const isCurrentPasswordValid = await bcrypt.compare(
        currentPassword,
        user.password,
      );
      if (!isCurrentPasswordValid) {
        logger.warn(`Invalid password change attempt for user: ${user.email}`, {
          userId,
        });
        res.status(400).json({
          success: false,
          message: "Current password is incorrect",
        } as APIResponse);
        return;
      }

      // Hash new password
      const saltRounds = 12;
      const hashedPassword = await bcrypt.hash(newPassword, saltRounds);

      // Update password
      user.password = hashedPassword;
      await user.save();

      logger.info(`Password changed successfully: ${user.email}`, {
        userId,
      });

      res.status(200).json({
        success: true,
        message: "Password changed successfully",
        data: { passwordChanged: true },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Change password error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get Privacy Policy
   * @route GET /api/v1/settings/privacy-policy
   * @access Private
   */
  getPrivacyPolicy(req: Request, res: Response): void {
    try {
      res.status(200).json({
        success: true,
        message: "Privacy policy retrieved successfully",
        data: {
          privacyPolicy: {
            title: "Privacy Policy",
            content:
              process.env.PRIVACY_POLICY_CONTENT ??
              "This is the privacy policy content. Please update PRIVACY_POLICY_CONTENT environment variable with your privacy policy text.",
            lastUpdated:
              process.env.PRIVACY_POLICY_UPDATED ?? new Date().toISOString(),
            version: process.env.PRIVACY_POLICY_VERSION ?? "1.0",
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get privacy policy error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get About Us
   * @route GET /api/v1/settings/about-us
   * @access Private
   */
  getAboutUs(req: Request, res: Response): void {
    try {
      res.status(200).json({
        success: true,
        message: "About us retrieved successfully",
        data: {
          aboutUs: {
            title: "About Us",
            content:
              process.env.ABOUT_US_CONTENT ??
              "This is the about us content. Please update ABOUT_US_CONTENT environment variable with your about us text.",
            lastUpdated:
              process.env.ABOUT_US_UPDATED ?? new Date().toISOString(),
            version: process.env.ABOUT_US_VERSION ?? "1.0",
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get about us error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get Terms & Conditions
   * @route GET /api/v1/settings/terms-conditions
   * @access Private
   */
  getTermsConditions(req: Request, res: Response): void {
    try {
      res.status(200).json({
        success: true,
        message: "Terms & conditions retrieved successfully",
        data: {
          termsConditions: {
            title: "Terms & Conditions",
            content:
              process.env.TERMS_CONDITIONS_CONTENT ??
              "This is the terms and conditions content. Please update TERMS_CONDITIONS_CONTENT environment variable with your terms and conditions text.",
            lastUpdated:
              process.env.TERMS_CONDITIONS_UPDATED ?? new Date().toISOString(),
            version: process.env.TERMS_CONDITIONS_VERSION ?? "1.0",
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get terms & conditions error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Submit Help & Feedback
   * @route POST /api/v1/settings/help-feedback
   * @access Private (all roles)
   * @body { type, category, subject, description, priority?, attachments? }
   */
  async submitHelpFeedback(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const {
        type = "feedback",
        category,
        subject,
        description,
        priority = "medium",
        attachments,
      } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!subject || !description) {
        res.status(400).json({
          success: false,
          message: "Subject and description are required",
        } as APIResponse);
        return;
      }

      let schoolId: mongoose.Types.ObjectId | undefined;

      switch (role) {
        case "student": {
          const student = await Student.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (student) {
            schoolId = student.schoolId;
          }
          break;
        }
        case "teacher": {
          const teacher = await Teacher.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (teacher) {
            schoolId = teacher.schoolId;
          }
          break;
        }
        case "school": {
          schoolId = req.user?.schoolId
            ? new mongoose.Types.ObjectId(req.user.schoolId)
            : undefined;
          break;
        }
        // No default
      }

      const helpFeedback = new HelpFeedback({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId,
        type: type === "help" ? "help" : "feedback",
        category,
        subject,
        description,
        priority,
        attachments: attachments ?? [],
        status: FEEDBACK_STATUS.PENDING,
      });

      await helpFeedback.save();

      logger.info(`Help/Feedback submitted: ${helpFeedback.id}`, {
        helpFeedbackId: helpFeedback.id,
        userId,
        type,
      });

      res.status(201).json({
        success: true,
        message: `${type === "help" ? "Help request" : "Feedback"} submitted successfully`,
        data: {
          helpFeedback: {
            id: helpFeedback.id,
            type: helpFeedback.type,
            category: helpFeedback.category,
            subject: helpFeedback.subject,
            status: helpFeedback.status,
            priority: helpFeedback.priority,
            createdAt: helpFeedback.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Submit help/feedback error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit help/feedback",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Submit Help Request From School (Student only)
   * @route POST /api/v1/settings/help-request-school
   * @access Private (Student)
   * @body { subject, description, priority?, attachments? }
   */
  async submitHelpRequestFromSchool(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const {
        subject,
        description,
        priority = "medium",
        attachments,
      } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (role !== "student") {
        res.status(403).json({
          success: false,
          message: "This endpoint is only available for students",
        } as APIResponse);
        return;
      }

      if (!subject || !description) {
        res.status(400).json({
          success: false,
          message: "Subject and description are required",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const helpFeedback = new HelpFeedback({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: student.schoolId,
        type: "help",
        category: "other",
        subject,
        description,
        priority,
        attachments: attachments ?? [],
        status: FEEDBACK_STATUS.PENDING,
      });

      await helpFeedback.save();

      logger.info(`Help request from school submitted: ${helpFeedback.id}`, {
        helpFeedbackId: helpFeedback.id,
        userId,
        studentId: student.id,
        schoolId: student.schoolId,
      });

      res.status(201).json({
        success: true,
        message: "Help request submitted successfully",
        data: {
          helpRequest: {
            id: helpFeedback.id,
            subject: helpFeedback.subject,
            status: helpFeedback.status,
            priority: helpFeedback.priority,
            createdAt: helpFeedback.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Submit help request from school error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit help request",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete Account
   * @route DELETE /api/v1/settings/account
   * @access Private (all roles)
   * @body { password, reason? }
   */
  async deleteAccount(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { password, reason } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Find user (include password for authentication)
      const user = await User.findById(userId).select("+password");
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Verify password if provided (for non-social users)
      if (user.password) {
        if (!password) {
          res.status(400).json({
            success: false,
            message: "Password is required to delete account",
          } as APIResponse);
          return;
        }

        const isPasswordValid = await bcrypt.compare(password, user.password);
        if (!isPasswordValid) {
          logger.warn(`Invalid password for account deletion: ${user.email}`, {
            userId,
          });
          res.status(400).json({
            success: false,
            message: "Invalid password",
          } as APIResponse);
          return;
        }
      }

      // Soft delete user
      await user.softDelete();

      // Soft delete role-specific profile
      switch (role) {
        case "student": {
          const student = await Student.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (student) {
            student.status = STUDENT_STATUS.WITHDRAWN;
            await student.save();
          }
          break;
        }
        case "teacher": {
          const teacher = await Teacher.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (teacher) {
            teacher.status = TEACHER_STATUS.TERMINATED;
            await teacher.save();
          }
          break;
        }
        case "school": {
          const school = await School.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (school) {
            school.status = SCHOOL_STATUS.SUSPENDED;
            await school.save();
          }
          break;
        }
        // No default
      }

      logger.info(`Account deleted: ${user.email}`, {
        userId,
        role,
        reason,
      });

      res.status(200).json({
        success: true,
        message: "Account deleted successfully",
        data: {
          accountDeleted: true,
          deletedAt: user.deletedAt,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Delete account error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete account",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

export const settingsController = new SettingsController();
